import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import brandConfig from '../../config/brandConfig';
import WhatsAppIcon from '../../components/atoms/WhatsAppIcon';
import { api } from '../../services/api';
import styles from './ContactPage.module.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', firmName: '', address: '', gst: '', message: '' });
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const phoneNumbers = brandConfig.phone_number.split(',').map(n => n.trim());
  const primaryPhone = phoneNumbers[0];

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // 1. Submit to Backend API
      const payload = {
        contactName: formData.name,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.firmName,
        subject: "Website Contact Form Inquiry",
        message: `${formData.message}\n\nAddress: ${formData.address}\nGST: ${formData.gst}`
      };
      
      await api.post('/enquiries', payload);

      // 2. Format WhatsApp message
      const message = `*NEW CONTACT ENQUIRY - ANAPURNA SALES*%0A%0A` +
        `*Name:* ${formData.name}%0A` +
        `*Email:* ${formData.email}%0A` +
        `*Firm:* ${formData.firmName}%0A` +
        `*Address:* ${formData.address}%0A` +
        `*GST:* ${formData.gst}%0A` +
        `*Phone:* ${formData.phone}%0A` +
        `*Message:* ${formData.message}%0A%0A` +
        `_Sent via website contact form._`;

      const cleanPhone = brandConfig.phone_number.replace(/\D/g, '');
      const whatsappUrl = `https://wa.me/${cleanPhone}?text=${message}`;

      // 3. Open WhatsApp
      window.open(whatsappUrl, '_blank');
      
      // 4. Success state
      setIsSuccess(true);
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      setError('Failed to send your message to the server. Please try reaching us directly via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <main className={styles.page}>
         <section className={styles.successWrapper}>
            <div className={styles.successCard}>
               <CheckCircle size={64} className={styles.successIcon} />
               <h1 className="heading-lg">Connected Successfully!</h1>
               <p>We've opened WhatsApp to connect you with our support desk. You can also call us directly using the button below.</p>
               <div className={styles.successActions}>
                 <a href={`tel:${primaryPhone}`} className={styles.callBtn}>Call Primary Line</a>
                 <button onClick={() => setIsSuccess(false)} className={styles.backBtn}>Back to Contact</button>
               </div>
            </div>
         </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      {/* 1. HERO SECTION */}
      <header className={styles.header}>
        <div className="container">
          <h1 className={`${styles.title} reveal`}>Contact Us</h1>
          <p className={`${styles.subtitle} reveal stagger-1`}>
            We're here to help with your wholesale and B2B queries.
          </p>
        </div>
      </header>

      <section className={styles.content}>
        <div className="container">
          
          {/* 2. QUICK CONTACT ACTIONS */}
          <div className={`${styles.quickActions} reveal stagger-2`}>
            <a href={`tel:${primaryPhone}`} className={styles.quickCard}>
              <div className={styles.quickIconWrapper}><Phone size={24} /></div>
              <h3 className={styles.quickTitle}>Call Us</h3>
              <p className={styles.quickText}>{primaryPhone}</p>
            </a>
            
            <a 
              href={brandConfig.whatsapp_chat_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${styles.quickCard} ${styles.whatsappCard}`}
            >
              <div className={`${styles.quickIconWrapper} ${styles.whatsappIconWrapper}`}>
                <WhatsAppIcon size={24} />
              </div>
              <h3 className={styles.quickTitle}>WhatsApp</h3>
              <p className={styles.quickText}>Instant Response</p>
            </a>
            
            <a href={`mailto:${brandConfig.email}`} className={styles.quickCard}>
              <div className={styles.quickIconWrapper}><Mail size={24} /></div>
              <h3 className={styles.quickTitle}>Email</h3>
              <p className={styles.quickText}>Within 24 Hours</p>
            </a>
          </div>

          <div className={`${styles.layout} reveal stagger-3`}>
            
            {/* 3. MAIN CONTACT INFO (LEFT) */}
            <div className={styles.infoSection}>
              <div className={styles.infoHighlight}>
                <h2 className={styles.sectionHeading}>Get In Touch</h2>
                <p className={styles.infoDesc}>
                  Whether you are looking for a new wholesale partner or have questions about our existing collections, our dedicated team is ready to assist you.
                </p>
              </div>

              <div className={styles.infoCardList}>
                <div className={styles.infoCard}>
                  <div className={styles.infoIcon}><MapPin size={22} /></div>
                  <div className={styles.infoCardContent}>
                    <h3 className={styles.infoCardLabel}>Corporate Office</h3>
                    <p className={styles.infoCardValue}>{brandConfig.business_address}</p>
                  </div>
                </div>

                <div className={styles.infoCard}>
                  <div className={styles.infoIcon}><Phone size={22} /></div>
                  <div className={styles.infoCardContent}>
                    <h3 className={styles.infoCardLabel}>Direct Line</h3>
                    <p className={styles.infoCardValue}>{brandConfig.phone_number}</p>
                  </div>
                </div>

                <div className={styles.infoCard}>
                  <div className={styles.infoIcon}><Mail size={22} /></div>
                  <div className={styles.infoCardContent}>
                    <h3 className={styles.infoCardLabel}>Email Address</h3>
                    <p className={styles.infoCardValue}>{brandConfig.email}</p>
                  </div>
                </div>

                {/* Highly Dominant WhatsApp Info Card */}
                <div className={`${styles.infoCard} ${styles.infoCardWhatsapp}`}>
                  <div className={styles.infoIconWhatsapp}><WhatsAppIcon size={22} /></div>
                  <div className={styles.infoCardContent}>
                    <h3 className={styles.infoCardLabel}>WhatsApp Support</h3>
                    <p className={styles.infoCardValue}>Chat on WhatsApp for instant response</p>
                    <a 
                      href={brandConfig.whatsapp_chat_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={styles.infoWhatsappLink}
                    >
                      {brandConfig.whatsapp_number}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. CONTACT FORM (RIGHT) */}
            <div className={styles.formSection}>
              <div className={styles.formCard}>
                <h2 className={styles.formHeading}>Send an Message</h2>
                <div className={styles.formHelper}>
                  <AlertCircle size={16} />
                  <span>Connect with us via WhatsApp for faster reply</span>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.inputGroup}>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      placeholder=" " 
                      className={styles.inputField}
                      required 
                      disabled={isSubmitting}
                    />
                    <label htmlFor="name" className={styles.floatingLabel}>Full Name</label>
                  </div>
                  
                  <div className={styles.inputGroup}>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      placeholder=" " 
                      className={styles.inputField}
                      required 
                      disabled={isSubmitting}
                    />
                    <label htmlFor="email" className={styles.floatingLabel}>Email Address</label>
                  </div>
                  
                  <div className={styles.inputGroup}>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      placeholder=" " 
                      className={styles.inputField}
                      required 
                    />
                    <label htmlFor="phone" className={styles.floatingLabel}>Phone Number</label>
                  </div>

                  <div className={styles.inputGroup}>
                    <input 
                      type="text" 
                      id="firmName" 
                      name="firmName" 
                      value={formData.firmName} 
                      onChange={handleChange} 
                      placeholder=" " 
                      className={styles.inputField}
                      required 
                    />
                    <label htmlFor="firmName" className={styles.floatingLabel}>Firm Name</label>
                  </div>

                  <div className={styles.inputGroup}>
                    <input 
                      type="text" 
                      id="address" 
                      name="address" 
                      value={formData.address} 
                      onChange={handleChange} 
                      placeholder=" " 
                      className={styles.inputField}
                      required 
                    />
                    <label htmlFor="address" className={styles.floatingLabel}>Address / City</label>
                  </div>

                  <div className={styles.inputGroup}>
                    <input 
                      type="text" 
                      id="gst" 
                      name="gst" 
                      value={formData.gst} 
                      onChange={handleChange} 
                      placeholder=" " 
                      className={styles.inputField}
                    />
                    <label htmlFor="gst" className={styles.floatingLabel}>GST Number (Optional)</label>
                  </div>
                  
                  <div className={styles.inputGroup}>
                    <textarea 
                      id="message" 
                      name="message" 
                      value={formData.message} 
                      onChange={handleChange} 
                      placeholder=" " 
                      rows="5"
                      className={`${styles.inputField} ${styles.textareaField}`}
                      required 
                      disabled={isSubmitting}
                    />
                    <label htmlFor="message" className={styles.floatingLabel}>Your Message / Requirements</label>
                  </div>
                  
                  {error && (
                    <div className={styles.errorMessage}>
                      <AlertCircle size={18} />
                      <span>{error}</span>
                    </div>
                  )}
                  
                  <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    <span>{isSubmitting ? 'Submitting...' : 'Submit & Chat on WhatsApp'}</span>
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
